import {  useState } from 'react'
import { ShellSnippet } from '@/shared/components/ShellSnippet'
import { HclSnippet, buildBackendHcl } from '@/shared/components/HclSnippet'
import type { OrgItem } from '@/lib/apiTypes'
import { s } from '@/features/integrations/guideStyles'
import type { GuideId } from '@/features/integrations/guides'
import { PlatformTabs, type Platform } from '@/features/integrations/components/PlatformTabs'
import { CurlSecretNote, CliCiNote } from '@/features/integrations/components/GuideNotes'
import {   CLI_INSTALL_GITHUB, CLI_GITLAB_SETUP, CLI_GITLAB_ID_TOKENS, TOKEN_EXCHANGE_CLI } from '@/features/integrations/snippets'

// ── Storing State ─────────────────────────────────────────────────────────────
export function StoringStateGuide({ org, token, onSwitchGuide }: { org: OrgItem | null; token: string | null; onSwitchGuide: (id: GuideId) => void }) {
  const [platform, setPlatform] = useState<Platform>('cli')
  const orgSlug = org?.slug ?? '<org-slug>'
  const orgId   = org?.id   ?? '<org-id>'
  const wsName  = '<workspace>'

  const backend = org
    ? buildBackendHcl(orgId, wsName, orgSlug, token)
    : `terraform {\n  backend "http" {\n    address        = "https://registry.terramantle.dev/state/<org-slug>/<workspace>"\n    lock_address   = "https://registry.terramantle.dev/state/<org-slug>/<workspace>.lock"\n    unlock_address = "https://registry.terramantle.dev/state/<org-slug>/<workspace>.lock"\n    username       = "terramantle-bot"\n  }\n}`

  const cliSnippet = `# Sign in once - the session is kept in your OS keyring and refreshed for you
terramantle auth login

# Pin the org and workspace you are working on
terramantle context set ${orgSlug} --org ${orgSlug} --workspace ${wsName}

# Export TF_HTTP_PASSWORD (and the registry TF_TOKEN_* vars) into this shell
eval "$(terramantle auth env --terraform)"

tofu init   # or: terraform init`

  const curlSnippet = `${TOKEN_EXCHANGE_CLI}

# macOS / Linux
export TF_HTTP_PASSWORD=$TOKEN
export TF_TOKEN_registry_terramantle_dev=$TF_HTTP_PASSWORD

# Windows CMD
# set TF_HTTP_PASSWORD=$TOKEN
# set TF_TOKEN_registry_terramantle_dev=$TOKEN

# PowerShell
# $env:TF_HTTP_PASSWORD = "$TOKEN"
# $env:TF_TOKEN_registry_terramantle_dev = "$TOKEN"

tofu init   # or: terraform init`

  const githubSnippet = `name: terraform

on:
  push:
    branches: [main]

permissions:
  contents: read
  id-token: write   # lets terramantle use the runner's ambient OIDC token

jobs:
  apply:
    runs-on: ubuntu-latest
    env:
      TERRAMANTLE_ORG: ${orgSlug}
    steps:
      - uses: actions/checkout@v4
      - uses: opentofu/setup-opentofu@v1

${CLI_INSTALL_GITHUB}

      - name: Terraform init & apply
        run: |
          eval "$(terramantle auth env --terraform)"
          tofu init
          tofu apply -auto-approve`

  const gitlabSnippet = `stages:
  - deploy

apply:
  stage: deploy
  image: ubuntu:24.04
${CLI_GITLAB_ID_TOKENS}
  variables:
    TERRAMANTLE_ORG: ${orgSlug}
${CLI_GITLAB_SETUP}
  script:
    - eval "$(terramantle auth env --terraform)"
    - tofu init
    - tofu apply -auto-approve
  only:
    - main`

  return (
    <div>
      <h3 style={s.guideTitle}>Storing State</h3>
      <p style={s.para}>
        Use Terramantle as a Terraform or OpenTofu HTTP backend natively. Each workspace is a unique URL path -
        state is versioned, scanned, and locked automatically.
      </p>

      <h3 style={s.subTitle}>Backend config (HCL)</h3>
      <HclSnippet code={backend} copyText={backend} />

      <h3 style={s.subTitle}>Authenticate, initialise and push state</h3>
      <p style={s.para}>
        The <button style={s.linkBtn} onClick={() => onSwitchGuide('cli-getting-started')}>Terramantle CLI</button>{' '}
        is the shortest path: it signs you in, refreshes the session, and exports the backend credential for you.
        Prefer the raw API? The <strong>curl</strong> tab does the token exchange by hand.
      </p>
      <PlatformTabs value={platform} onChange={setPlatform} />
      {platform === 'cli'    && <ShellSnippet code={cliSnippet}    language="bash" />}
      {platform === 'curl'   && <ShellSnippet code={curlSnippet}   language="bash" />}
      {platform === 'github' && <ShellSnippet code={githubSnippet} language="yaml" />}
      {platform === 'gitlab' && <ShellSnippet code={gitlabSnippet} language="yaml" />}

      {platform === 'cli'  && (
        <p style={{ ...s.para, marginTop: 10 }}>
          Not installed yet? See{' '}
          <button style={s.linkBtn} onClick={() => onSwitchGuide('cli-getting-started')}>Getting Started</button>, or{' '}
          <button style={s.linkBtn} onClick={() => onSwitchGuide('cli-auth')}>Signing In</button> for the other
          shells and the full set of variables <code style={s.code}>auth env</code> exports.
        </p>
      )}
      {platform === 'curl' && <CurlSecretNote />}
      {(platform === 'github' || platform === 'gitlab') && <CliCiNote onSwitchGuide={onSwitchGuide} />}
    </div>
  )
}
